import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { createCheckoutSession } from '../services/stripe';
import { getPendingCheckout, clearPendingCheckout } from '../utils/pendingCheckout';
import { performEmergencyCleanup, isStorageNearQuota } from '../utils/localStorageCleanup';

function getFriendlyErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('bad_code_verifier') || msg.includes('invalid_grant')) {
      return 'Your sign-in session expired. Please try again.';
    }
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: string }).code;
    if (code === 'bad_code_verifier' || code === 'invalid_grant') {
      return 'Your sign-in session expired. Please try again.';
    }
  }
  return 'Something went wrong during sign-in. Please try again.';
}

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Completing sign in...');
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const handleAuthCallback = async () => {
      try {
        // Pre-callback localStorage cleanup to prevent quota errors during session exchange
        try {
          if (isStorageNearQuota()) {
            console.log('🧹 [AuthCallback] localStorage near quota, performing emergency cleanup');
            const result = performEmergencyCleanup();
            console.log(`🧹 [AuthCallback] Cleanup freed ${result.freedMB.toFixed(2)} MB`);
          }
        } catch (cleanupError) {
          console.warn('Failed to perform pre-callback cleanup:', cleanupError);
        }

        // Exchange code for session
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');

        if (!code) {
          console.error('Auth callback error: no code parameter in URL');
          setError('Sign-in link was incomplete. Please try again.');
          return;
        }

        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

        if (exchangeError) {
          console.error('Auth callback error:', exchangeError);
          setError(getFriendlyErrorMessage(exchangeError));
          return;
        }

        // Verify session actually persisted before navigating
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          console.error('Auth callback error: session not persisted after exchange');
          setError('Something went wrong during sign-in. Please try again.');
          return;
        }

        // Check for pending checkout intent (auto-expires after 2 minutes)
        const pendingPriceId = getPendingCheckout();

        if (pendingPriceId && session.access_token) {
          // User signed in with intent to checkout
          setStatus('Redirecting to checkout...');

          try {
            const { url } = await createCheckoutSession(pendingPriceId, session.access_token);
            // Clean up before redirect
            clearPendingCheckout();
            // Redirect to Stripe Checkout
            window.location.href = url;
          } catch (checkoutError) {
            console.error('Checkout initiation error:', checkoutError);
            // Clean up and redirect home on error
            clearPendingCheckout();
            navigate('/');
          }
        } else {
          // Normal sign-in flow, redirect to home
          navigate('/');
        }
      } catch (err) {
        console.error('Unexpected auth callback error:', err);
        setError(getFriendlyErrorMessage(err));
      }
    };

    handleAuthCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md px-4">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-foreground text-lg mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Try again
          </button>
          <div className="mt-4">
            <a href="/" className="text-muted-foreground text-sm underline hover:text-foreground transition-colors">
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-foreground">{status}</p>
      </div>
    </div>
  );
};
